var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Events } from "@auvious/utils";
import { ApiCommandResource } from "./command/ApiCommandResource";
import { ApiQueryResource } from "./query/ApiQueryResource";
import { logger } from "./logger";
export * from "./query/ApiQueryResource";
export * from "./command/ApiCommandResource";
export * from "./model/model";
export var ReconnectionReason;
(function (ReconnectionReason) {
    /** Any error that will cause connection recovery */
    ReconnectionReason["Error"] = "Error";
    /** Transcription initiated remotely while already enabled */
    ReconnectionReason["TranscriptRequested"] = "TranscriptRequested";
})(ReconnectionReason || (ReconnectionReason = {}));
var ApiState;
(function (ApiState) {
    ApiState[ApiState["Enabling"] = 0] = "Enabling";
    ApiState[ApiState["Enabled"] = 1] = "Enabled";
    ApiState[ApiState["Disabled"] = 2] = "Disabled";
})(ApiState || (ApiState = {}));
var AsrApiEvents;
(function (AsrApiEvents) {
    AsrApiEvents["TranscriptCreated"] = "TranscriptCreatedEvent";
    AsrApiEvents["TranscriptRequested"] = "TranscriptRequestedEvent";
    AsrApiEvents["ConnectionCreated"] = "ConnectionCreatedEvent";
    AsrApiEvents["ConnectionFailed"] = "ConnectionFailedEvent";
    AsrApiEvents["TranscriptFailed"] = "TranscriptFailedEvent";
    AsrApiEvents["TranslationFailed"] = "TranslationFailedEvent";
})(AsrApiEvents || (AsrApiEvents = {}));
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export class Asr {
    constructor(client, options) {
        this.client = client;
        this.options = options;
        this.events = new Events();
        this.state = ApiState.Disabled;
        this.apiCommandResource = new ApiCommandResource(this.client);
        this.apiQueryResource = new ApiQueryResource(this.client);
        this.options.assetsPath = this.options.assetsPath.replace(/^\/|\/$/g, "");
        this.muted = false;
        this.languageCode = "";
        this.client.events.setEventHandlers({
            onMessageReceived: (ev) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                switch (ev.payload.type) {
                    case AsrApiEvents.TranscriptCreated: {
                        for (let i = 0; i < ev.payload.translations.length; i++) {
                            if (this.languageCode === ev.payload.translations[i].languageCode) {
                                this.events.emit("transcript", {
                                    userId: ev.payload.userId,
                                    transcript: ev.payload.translations[i].transcript,
                                    isFinal: ev.payload.isFinal,
                                });
                                break;
                            }
                        }
                        break;
                    }
                    case AsrApiEvents.TranscriptRequested: {
                        this.transcriptId = ev.payload.transcriptId;
                        // if already enabled, close ws to trigger reconnection
                        if (this.state !== ApiState.Disabled) {
                            logger("Requested new transcript while already active. Reconnecting.");
                            (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close(1000, ReconnectionReason.TranscriptRequested);
                        }
                        else {
                            logger("Requested new transcript. Enabling.");
                            this.state = ApiState.Enabling;
                            // TODO: notify client and agent of failure to enable
                            yield this.setupAudio();
                            this.connect();
                        }
                        break;
                    }
                    case AsrApiEvents.ConnectionCreated: {
                        if (this.state === ApiState.Enabling) {
                            logger("Connection created. Ready to transmit audio.");
                            this.state = ApiState.Enabled;
                            this.events.emit("ready");
                        }
                        else {
                            logger("Got ConnectionCreated while not enabling.");
                        }
                        break;
                    }
                    case AsrApiEvents.ConnectionFailed: {
                        // in this case websocket should have been closed and hanled by reconnection
                        logger("Connection failed.");
                        break;
                    }
                    case AsrApiEvents.TranscriptFailed: {
                        logger("Transcript failed.");
                        yield this.disable();
                        this.events.emit("transcriptFailed");
                        break;
                    }
                    case AsrApiEvents.TranslationFailed: {
                        logger("Translation failed.");
                        this.events.emit("translationFailed");
                        break;
                    }
                }
            }),
        });
    }
    isMuted() {
        return this.muted;
    }
    isEnabled() {
        return this.state === ApiState.Enabled;
    }
    startTranslate(languageCode) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.apiCommandResource.startTranslation(this.transcriptId, languageCode);
            this.languageCode = languageCode;
        });
    }
    stopTranslate() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.apiCommandResource.stopTranslation();
            this.languageCode = "";
        });
    }
    enable(organizationLanguageId, translate, record) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.state !== ApiState.Disabled) {
                return;
            }
            logger("Enabling for org ", organizationLanguageId);
            this.state = ApiState.Enabling;
            yield this.setupAudio();
            const response = yield this.apiCommandResource.createUserTrancriptConfiguration(organizationLanguageId, this.options.applicationId, this.options.conferenceId, !!translate, !!record);
            this.transcriptId = response.transcriptId;
            yield this.startTranslate(response.languageCode);
            yield this.connect();
        });
    }
    /**
     * Changes stream set for transcription.
     * If null or no audio tracks included, transcription is paused
     * and should be called again with valid stream to resume.
     */
    setStream(stream) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.worker) {
                return false;
            }
            const audio = stream === null || stream === void 0 ? void 0 : stream.getAudioTracks()[0];
            if (!audio) {
                this.muted = true;
                this.pauseAudio();
                return false;
            }
            else {
                this.muted = false;
                const sampleRates = audio.getSettings().sampleRate === 44100
                    ? [44100, 48000]
                    : [48000, 44100];
                try {
                    this.source = this.audioContext.createMediaStreamSource(stream);
                }
                catch (ex) {
                    this.source = yield this.createSourceFallback(ex, sampleRates[0], stream).catch((e) => this.createSourceFallback(e, sampleRates[1], stream));
                }
                this.worker.port.start();
                this.resumeAudio();
                return true;
            }
        });
    }
    disable() {
        return __awaiter(this, void 0, void 0, function* () {
            this.state = ApiState.Disabled;
            this.pauseAudio();
            if (this.ws) {
                this.ws.close();
                this.ws = null;
            }
            if (this.worker) {
                this.worker.port.close();
                this.worker.disconnect();
                if (this.audioContext && this.audioContext.state !== "closed") {
                    yield this.audioContext.close();
                }
                this.audioContext = null;
                this.worker = null;
                this.source = null;
            }
            this.stopTranslate();
            logger("Asr has been disabled.");
        });
    }
    connect(reconnecting = 0) {
        return __awaiter(this, void 0, void 0, function* () {
            this.ws = new WebSocket(`wss://${location.host}/api/asr/transcript`);
            this.ws.onopen = () => __awaiter(this, void 0, void 0, function* () {
                logger("Connected to asr websocket.");
                this.state = ApiState.Enabling;
                this.send(JSON.stringify({
                    transcriptId: this.transcriptId,
                    accessToken: this.client.auth.token.accessToken,
                    sampleRate: this.audioContext.sampleRate,
                }));
                if (reconnecting) {
                    reconnecting = 0;
                    this.events.emit("reconnected");
                }
                this.resumeAudio();
            });
            this.ws.onerror = (err) => {
                var _a;
                logger("Error on asr websocket: ", err);
                (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close();
            };
            this.ws.onclose = (event) => __awaiter(this, void 0, void 0, function* () {
                this.pauseAudio();
                if (this.state !== ApiState.Disabled) {
                    const delay = ++reconnecting * 1000;
                    this.events.emit("reconnecting", {
                        delay,
                        times: reconnecting,
                        reason: event.reason === "TranscriptRequested"
                            ? ReconnectionReason.TranscriptRequested
                            : ReconnectionReason.Error,
                    });
                    logger("Websocket closed. Reconnecting in ", delay, "ms.");
                    yield sleep(delay);
                    this.connect(reconnecting);
                }
            });
        });
    }
    setupAudio(sampleRate = 16000) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            // close if previously set up
            (_a = this.worker) === null || _a === void 0 ? void 0 : _a.port.close();
            (_b = this.worker) === null || _b === void 0 ? void 0 : _b.disconnect();
            if (this.audioContext && this.audioContext.state !== "closed") {
                yield this.audioContext.close();
            }
            // eslint-disable-next-line compat/compat
            this.audioContext = new AudioContext({ sampleRate });
            yield this.audioContext.audioWorklet.addModule(`${location.origin}/${this.options.assetsPath}/stt.worker.js`);
            // eslint-disable-next-line compat/compat
            this.worker = new AudioWorkletNode(this.audioContext, "stt-worker", {
                outputChannelCount: [1],
            });
            this.worker.port.onmessage = (event) => this.send(event.data);
            logger("Audio Worker has been set up.");
        });
    }
    createSourceFallback(ex, sampleRate, stream) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!(ex instanceof DOMException && ex.name === "NotSupportedError")) {
                throw ex;
            }
            logger("Preferred sample rate not supported. Falling back to ", sampleRate);
            yield this.setupAudio(sampleRate);
            const source = this.audioContext.createMediaStreamSource(stream);
            // reopen websocket
            this.state = ApiState.Disabled;
            this.ws.close();
            yield this.connect();
            return source;
        });
    }
    pauseAudio() {
        var _a;
        if (this.muted) {
            logger("Pausing audio.");
            (_a = this.source) === null || _a === void 0 ? void 0 : _a.disconnect();
        }
    }
    resumeAudio() {
        var _a;
        if (!this.muted) {
            logger("Resuming audio");
            (_a = this.source) === null || _a === void 0 ? void 0 : _a.connect(this.worker);
        }
    }
    send(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.ws.readyState === this.ws.OPEN) {
                    this.ws.send(msg);
                }
                else {
                    logger("Cannot send message. WebSocket is not open.");
                }
            }
            catch (err) {
                logger(err);
            }
        });
    }
}
/** should include when requesting mediastreams for audio optimal compatibility */
Asr.AudioTrackConstraints = {
    sampleRate: 16000,
    sampleSize: 16,
    // the worker already looks for the first audio track of its first and only node, inputs[node=0][channel=0]
    // channelCount: 1,
};
